import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useContracts, useUpdateContract, useDeleteContract, useSendContract, Contract } from "@/hooks/useContracts";
import { useDeals } from "@/hooks/useDeals";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MoreHorizontal, Send, Eye, Trash2, CheckCircle, Copy, Shield } from "lucide-react";
import { format, isWithinInterval } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { BulkContractActions } from "./BulkContractActions";
import { ContractFilters, ContractFilters as FiltersType } from "./ContractFilters";

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  draft: { label: "Draft", variant: "secondary" },
  sent: { label: "Sent", variant: "default" },
  viewed: { label: "Viewed", variant: "outline" },
  signed: { label: "Signed", variant: "default" },
  expired: { label: "Expired", variant: "destructive" },
};

const approvalStatusConfig: Record<string, { label: string; color: string }> = {
  pending: { label: "Pending Approval", color: "text-amber-500" },
  approved: { label: "Approved", color: "text-green-500" },
  rejected: { label: "Rejected", color: "text-red-500" },
};

export function ContractsTable() {
  const navigate = useNavigate();
  const { data: contracts, isLoading } = useContracts();
  const { data: deals } = useDeals();
  const updateContract = useUpdateContract();
  const deleteContract = useDeleteContract();
  const sendContract = useSendContract();
  
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<string | null>(null);
  const [signerEmail, setSignerEmail] = useState("");
  const [selectedContracts, setSelectedContracts] = useState<Contract[]>([]);
  const [filters, setFilters] = useState<FiltersType>({
    search: "",
    status: null,
    dateRange: undefined,
    signerEmail: "",
    dealId: null,
  });

  // Filter contracts
  const filteredContracts = useMemo(() => {
    if (!contracts) return [];
    
    return contracts.filter(contract => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          contract.title.toLowerCase().includes(searchLower) ||
          contract.signer_name?.toLowerCase().includes(searchLower) ||
          contract.signer_email?.toLowerCase().includes(searchLower) ||
          contract.deals?.title?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (filters.status) {
        // Handle "pending" as approval_status check
        if (filters.status === "pending") {
          const contractWithApproval = contract as any;
          if (contractWithApproval.approval_status !== "pending") return false;
        } else if (contract.status !== filters.status) {
          return false;
        }
      }
      
      // Date range filter
      if (filters.dateRange?.from) {
        const contractDate = new Date(contract.created_at);
        const from = filters.dateRange.from;
        const to = filters.dateRange.to || filters.dateRange.from;
        if (!isWithinInterval(contractDate, { start: from, end: to })) {
          return false;
        }
      }
      
      // Signer email filter
      if (filters.signerEmail) {
        if (!contract.signer_email?.toLowerCase().includes(filters.signerEmail.toLowerCase())) {
          return false;
        }
      }
      
      // Deal filter
      if (filters.dealId) {
        if (contract.deal_id !== filters.dealId) return false;
      }
      
      return true;
    });
  }, [contracts, filters]);

  const handleSend = (contractId: string) => {
    setSelectedContract(contractId);
    setSendDialogOpen(true);
  };

  const confirmSend = () => {
    if (selectedContract && signerEmail) {
      sendContract.mutate({ id: selectedContract, signer_email: signerEmail });
      setSendDialogOpen(false);
      setSignerEmail("");
      setSelectedContract(null);
    }
  };

  const handleMarkSigned = async (contractId: string) => {
    try {
      await updateContract.mutateAsync({ 
        id: contractId, 
        status: "signed", 
        signed_at: new Date().toISOString() 
      });
      
      await supabase.functions.invoke('contract-signed-notification', {
        body: { contractId },
      });
      
      toast.success("Contract signed and notification sent");
    } catch (error) {
      console.error("Error marking contract as signed:", error);
      toast.error("Failed to process contract signing");
    }
  };

  const copySigningLink = (signingToken: string) => {
    const link = `${window.location.origin}/sign?token=${signingToken}`;
    navigator.clipboard.writeText(link);
    toast.success("Signing link copied to clipboard");
  };

  const toggleSelectContract = (contract: Contract) => {
    setSelectedContracts(prev => {
      const isSelected = prev.some(c => c.id === contract.id);
      if (isSelected) {
        return prev.filter(c => c.id !== contract.id);
      }
      return [...prev, contract];
    });
  };

  const toggleSelectAll = () => {
    if (!filteredContracts) return;
    if (selectedContracts.length === filteredContracts.length) {
      setSelectedContracts([]);
    } else {
      setSelectedContracts([...filteredContracts]);
    }
  };

  const clearSelection = () => {
    setSelectedContracts([]);
  };

  // Prepare deals for filter dropdown
  const dealOptions = useMemo(() => {
    return deals?.map(d => ({ id: d.id, title: d.title })) || [];
  }, [deals]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  const isAllSelected = filteredContracts && filteredContracts.length > 0 && selectedContracts.length === filteredContracts.length;
  const isSomeSelected = selectedContracts.length > 0 && selectedContracts.length < (filteredContracts?.length || 0);

  return (
    <>
      <ContractFilters 
        filters={filters} 
        onFiltersChange={setFilters} 
        deals={dealOptions}
      />
      
      <BulkContractActions 
        selectedContracts={selectedContracts} 
        onClearSelection={clearSelection} 
      />
      
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">
              <Checkbox 
                checked={isAllSelected}
                onCheckedChange={toggleSelectAll}
                aria-label="Select all"
                className={isSomeSelected ? "opacity-50" : ""}
              />
            </TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Deal</TableHead>
            <TableHead>Signer</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Sent</TableHead>
            <TableHead>Signed</TableHead>
            <TableHead className="w-[70px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredContracts?.map((contract) => {
            const isSelected = selectedContracts.some(c => c.id === contract.id);
            const contractWithApproval = contract as any;
            const needsApproval = contractWithApproval.approval_required && 
              contractWithApproval.approval_status === "pending";
            const approvalRejected = contractWithApproval.approval_status === "rejected";
            
            return (
              <TableRow key={contract.id} className={isSelected ? "bg-primary/5" : ""}>
                <TableCell>
                  <Checkbox 
                    checked={isSelected}
                    onCheckedChange={() => toggleSelectContract(contract)}
                    aria-label={`Select ${contract.title}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{contract.title}</span>
                    {needsApproval && (
                      <Badge variant="outline" className="gap-1 text-amber-500 border-amber-500/30">
                        <Shield className="h-3 w-3" />
                        Pending Approval
                      </Badge>
                    )}
                    {approvalRejected && (
                      <Badge variant="outline" className="gap-1 text-red-500 border-red-500/30">
                        <Shield className="h-3 w-3" />
                        Rejected
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>{contract.deals?.title || "-"}</TableCell>
                <TableCell>
                  {contract.signer_name || contract.signer_email || "-"}
                </TableCell>
                <TableCell>
                  <Badge variant={statusConfig[contract.status]?.variant || "secondary"}>
                    {statusConfig[contract.status]?.label || contract.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {contract.sent_at ? format(new Date(contract.sent_at), "MMM d, yyyy") : "-"}
                </TableCell>
                <TableCell>
                  {contract.signed_at ? format(new Date(contract.signed_at), "MMM d, yyyy") : "-"}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/app/contracts/${contract.id}`)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View & Edit
                      </DropdownMenuItem>
                      {contract.status === "draft" && !needsApproval && !approvalRejected && (
                        <DropdownMenuItem onClick={() => handleSend(contract.id)}>
                          <Send className="h-4 w-4 mr-2" />
                          Send for Signature
                        </DropdownMenuItem>
                      )}
                      {(contract.status === "sent" || contract.status === "viewed") && (
                        <>
                          <DropdownMenuItem onClick={() => handleMarkSigned(contract.id)}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Mark as Signed
                          </DropdownMenuItem>
                          {contract.signing_token && (
                            <DropdownMenuItem onClick={() => copySigningLink(contract.signing_token!)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Copy Signing Link
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                      <DropdownMenuItem 
                        className="text-destructive"
                        onClick={() => deleteContract.mutate(contract.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
          {filteredContracts?.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                {contracts?.length === 0 ? "No contracts yet" : "No contracts match your filters"}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Contract for Signature</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="signer-email">Signer Email</Label>
              <Input
                id="signer-email"
                type="email"
                placeholder="client@example.com"
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmSend} disabled={!signerEmail}>
              <Send className="h-4 w-4 mr-2" />
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
